import { useRef, useState, useEffect } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import { Badge, Box, Typography, IconButton, Button, Divider, Stack, Avatar } from '@mui/material';
import { alpha } from '@mui/material/styles';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import Iconify from './Iconify';
import MenuPopover from './MenuPopover';
import { DOMAIN } from '../redux/constants';
import { fToNow } from '../utils/formatTime';
import { RED } from '../utils/brandColors';
import buildAuthHeaders from '../utils/apiHeaders';
import { getNotifMeta, getNotifDestination, cleanMessage } from '../utils/notificationHelpers';
import { useToast } from '../context/ToastContext';
import { useWs } from '../context/WsContext';
import DailyQuestCard from './DailyQuestCard';

// Window event dispatched by any surface that marks notifications read
// outside this component (e.g. the /notifications page). The bell listens
// and refetches its unread count so the badge can't go stale. The backend
// read endpoints don't push a WS message back, so without this the badge
// keeps its old count until popover-open or WS reconnect.
export const NOTIFICATIONS_READ_EVENT = 'notifications-read';

export default function NotificationBell() {
  const anchorRef = useRef(null);
  const navigate = useNavigate();
  const showToast = useToast();
  const ws = useWs();
  const [open, setOpen] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(false);

  const { userInfo } = useSelector((state) => state.userLogin);
  const accessToken = userInfo?.access;

  // Fetch initial unread count on mount. Key on the access token instead of
  // the full userInfo ref so unrelated Redux updates (avatar refresh, follower
  // count bumps, username rename) don't retrigger the fetch and race the WS.
  useEffect(() => {
    if (!accessToken) return;
    let cancelled = false;
    axios
      .get(`${DOMAIN}/api/v1/engagement/notifications/unread-count/`, { headers: { Authorization: `Bearer ${accessToken}` } })
      .then((res) => { if (!cancelled) setUnreadCount(Number(res.data.unread_count) || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [accessToken]);

  // Notification pushes now arrive over the single app-wide socket owned by
  // WsProvider. The connect/backoff/bfcache machinery that used to live here
  // moved there verbatim; what remains is bell-local: dedup-by-id, the unread
  // counter, and the challenge_invite auto-open.
  useEffect(() => {
    if (!accessToken || !ws) return undefined;

    const offMessage = ws.subscribe('notification', (msg) => {
      if (!msg.notification) return;
      const notif = msg.notification;
      // Dedup by id so WS reconnects / duplicate pushes don't
      // inflate the unread count or re-trigger the auto-open.
      let isNew = false;
      setNotifications((prev) => {
        if (prev.some((n) => n.id === notif.id)) return prev;
        isNew = true;
        return [notif, ...prev];
      });
      if (isNew) {
        setUnreadCount((prev) => prev + 1);
        // Only auto-open on FRESH unread challenge_invites (arrived now,
        // not a stale re-delivery from WS reconnect). Checking !is_read
        // guards against the backend ever pushing a pre-read record.
        if (notif.type === 'challenge_invite' && !notif.is_read && anchorRef.current) {
          setOpen(anchorRef.current);
        }
      }
    });

    // Re-fetch unread count to sync any missed notifications while
    // disconnected. This is a connection-lifecycle concern, not a message,
    // so it hangs off subscribeOpen and fires on every (re)connect exactly
    // as the old ws.onopen handler did.
    let cancelled = false;
    const offOpen = ws.subscribeOpen(() => {
      axios
        .get(`${DOMAIN}/api/v1/engagement/notifications/unread-count/`, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then((res) => { if (!cancelled) setUnreadCount(Number(res.data.unread_count) || 0); })
        .catch(() => {});
    });

    return () => {
      cancelled = true;
      offMessage();
      offOpen();
    };
  }, [accessToken, ws]);

  // Re-sync the badge when another surface (the /notifications page) marks
  // items read. Refetches from the server rather than trusting event payloads
  // so partial reads (single item, group) land on the exact remaining count.
  useEffect(() => {
    if (!accessToken) return undefined;
    let cancelled = false;
    const onExternalRead = () => {
      axios
        .get(`${DOMAIN}/api/v1/engagement/notifications/unread-count/`, { headers: { Authorization: `Bearer ${accessToken}` } })
        .then((res) => { if (!cancelled) setUnreadCount(Number(res.data.unread_count) || 0); })
        .catch(() => {});
    };
    window.addEventListener(NOTIFICATIONS_READ_EVENT, onExternalRead);
    return () => {
      cancelled = true;
      window.removeEventListener(NOTIFICATIONS_READ_EVENT, onExternalRead);
    };
  }, [accessToken]);

  if (!userInfo) return null;

  const handleOpen = (event) => {
    setOpen(event.currentTarget);
    setLoading(true);
    axios
      .get(`${DOMAIN}/api/v1/engagement/notifications/`, buildAuthHeaders(userInfo))
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data.results || [];
        setNotifications(data);
        // Sync unread count from fresh data
        setUnreadCount(data.filter((n) => !n.is_read).length);
      })
      .catch(() => { if (showToast) showToast('Failed to load notifications', 'error'); })
      .finally(() => setLoading(false));
  };

  const handleClose = () => setOpen(null);

  const handleMarkAllRead = () => {
    axios
      .post(`${DOMAIN}/api/v1/engagement/notifications/read/`, {}, buildAuthHeaders(userInfo))
      .then(() => {
        setUnreadCount(0);
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      })
      .catch(() => { if (showToast) showToast('Failed to mark notifications as read', 'error'); });
  };

  const handleNotifClick = (notif) => {
    // Optimistic UI update
    if (!notif.is_read) {
      setNotifications((prev) => prev.map((n) => n.id === notif.id ? { ...n, is_read: true } : n));
      setUnreadCount((prev) => Math.max(0, prev - 1));
      // Persist to backend
      axios.post(`${DOMAIN}/api/v1/engagement/notifications/${notif.id}/read/`, {}, buildAuthHeaders(userInfo)).catch(() => {});
    }
    handleClose();
    const dest = getNotifDestination(notif);
    if (dest) navigate(dest);
  };

  return (
    <>
      <IconButton
        ref={anchorRef}
        onClick={handleOpen}
        aria-label={unreadCount > 0 ? `Notifications, ${unreadCount} unread` : 'Notifications'}
        sx={{
          color: 'text.primary',
          transition: 'all 0.2s ease',
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <Iconify icon="eva:bell-fill" width={24} height={24} />
        </Badge>
      </IconButton>

      <MenuPopover
        open={Boolean(open)}
        anchorEl={open}
        onClose={handleClose}
        sx={{
          p: 0,
          width: { xs: '100vw', sm: 340 },
          maxWidth: { xs: '100vw', sm: 340 },
          borderRadius: { xs: 0, sm: '12px' },
          bgcolor: { xs: 'background.default', sm: 'background.paper' },
          // Kill the Popover Paper's default elevation shadow on mobile so
          // there's no glow where the drawer meets the navbar. A bottom-only
          // drop shadow gives lift without the top glow. Desktop keeps the
          // default Popover shadow for its floating-card look.
          boxShadow: { xs: '0 4px 12px rgba(0,0,0,0.06)', sm: '0 5px 5px -3px rgba(0,0,0,0.12), 0 8px 10px 1px rgba(0,0,0,0.08), 0 3px 14px 2px rgba(0,0,0,0.06)' },
          // On mobile, hide the arrow decoration — the panel reads as a
          // full-width drawer anchored to the navbar's bottom edge.
          '& .arrow': { display: { xs: 'none', sm: 'block' } },
        }}
        marginThreshold={0}
      >
        <Box sx={{ px: 2.5, py: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box>
            <Typography variant="subtitle1" fontWeight={700}>Notifications</Typography>
            {unreadCount > 0 && (
              <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                {unreadCount} unread
              </Typography>
            )}
          </Box>
          {unreadCount > 0 && (
            <Button size="small" color="primary" onClick={handleMarkAllRead} sx={{ textTransform: 'none', fontWeight: 600, fontSize: '0.75rem' }}>
              Read all
            </Button>
          )}
        </Box>

        <Divider sx={{ display: { xs: 'none', sm: 'block' } }} />

        <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
          <DailyQuestCard compact />
        </Box>

        <Stack sx={{ maxHeight: 360, overflowY: 'auto' }}>
          {loading && (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>Loading...</Typography>
            </Box>
          )}

          {!loading && notifications.length === 0 && (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotificationsNoneIcon sx={{ fontSize: 36, color: 'text.disabled', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No notifications yet
              </Typography>
            </Box>
          )}

          {!loading && notifications.map((notif) => {
            const meta = getNotifMeta(notif.type);
            const dest = getNotifDestination(notif);
            const isInvite = notif.type === 'challenge_invite' && !notif.is_read;

            return (
              <Box
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                sx={{
                  px: 2, py: 1.5, mx: 0.5,
                  display: 'flex', alignItems: 'flex-start', gap: 1.5,
                  cursor: dest ? 'pointer' : 'default',
                  borderRadius: '8px',
                  bgcolor: (() => { if (isInvite) return alpha(RED, 0.06); if (notif.is_read) return 'transparent'; return 'action.hover'; })(),
                  border: isInvite ? `1px solid ${alpha(RED, 0.2)}` : '1px solid transparent',
                  '&:hover': { bgcolor: isInvite ? alpha(RED, 0.1) : 'action.selected' },
                  mb: isInvite ? 0.5 : 0,
                }}
              >
                <Avatar sx={{ width: 32, height: 32, bgcolor: meta.bg, color: meta.color, flexShrink: 0 }}>
                  {meta.icon}
                </Avatar>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography variant="body2" sx={{ fontWeight: notif.is_read ? 400 : 600, lineHeight: 1.4 }}>
                    {cleanMessage(notif.message)}
                  </Typography>
                  {isInvite ? (
                    <Button
                      size="small"
                      variant="contained"
                      color="primary"
                      onClick={(e) => { e.stopPropagation(); handleNotifClick(notif); }}
                      sx={{
                        mt: 0.5, px: 2, py: 0.25, borderRadius: '8px',
                        textTransform: 'none',
                        fontWeight: 700, fontSize: '0.75rem',
                      }}
                    >
                      Accept
                    </Button>
                  ) : (
                    <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                      {notif.created_at ? fToNow(notif.created_at) : ''}
                    </Typography>
                  )}
                </Box>
                {!notif.is_read && !isInvite && (
                  <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: RED, flexShrink: 0, mt: 0.75 }} />
                )}
              </Box>
            );
          })}
        </Stack>

        <Divider sx={{ display: { xs: 'none', sm: 'block' } }} />
        <Box sx={{ p: 1 }}>
          <Button
            fullWidth
            size="small"
            component={RouterLink}
            to="/notifications"
            onClick={handleClose}
            sx={{ color: 'text.secondary', textTransform: 'none', fontWeight: 600 }}
          >
            View all notifications
          </Button>
        </Box>
      </MenuPopover>
    </>
  );
}
